import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import gql from 'graphql-tag';

import {
  InverterV2,
  MyCertifiedPlant,
  VirtualRTAzzurroHubTarget,
  VirtualRTStateType,
  VirtualRTTelemetryType,
} from '../telemetry';
import {
  OnVirtualRTTelemetrySubscription,
} from '../telemetry/index.graph-ql';
import { VirtualRTMockService, VIRTUAL_RT_MOCK_ENABLED } from './virtual-rt-mock.service';
import { SystemEnergyState } from '../models/telemetry.model';
import { StateService } from '../state/state.service';
import { CmdAzzurroHubService } from '../cmd-azzurro-hub/cmd-azzurro-hub.service';
import { CmdConnextService } from '../cmd-connext/cmd-connext.service';
import { normalizeVirtualRTStatus, normalizeRTTimestamp } from './real-time.utils';

export type RTMonitoringType = 'AzzurroHub' | 'Connext';

const virtualRTTelemetryFragment = gql`
  fragment virtualrttelemetrytype on VirtualRTTelemetryType {
    deviceId
    loggerId
    target
    ZZ {
      ALERTS
      BP
      EG1
      EG2
      EG3
      EG4
      EG5
      EG6
      ET1
      ET2
      ET3
      ET4
      ET5
      ET6
      GwId
      P_B_AGGR
      P_DC_TOT
      P_EXP
      P_EXT
      P_LOAD
      PK
      SOL_EG
      SOL_IRR
      SOL_TEMP
      TS
      TZ
    }
  }
`;

const WAIT_STATUS_TIMEOUT_MS = 10000;

@Injectable({
  providedIn: 'root',
})
export class RealTimeTelemetryService {
  private static readonly DEBUG = false;
  private static readonly TAG = '[RT-DEBUG][Service]';

  constructor(
    private readonly cmdAzzurroHubService: CmdAzzurroHubService,
    private readonly cmdConnextService: CmdConnextService,
    private readonly onVirtualRTTelemetrySubscription: OnVirtualRTTelemetrySubscription,
    private readonly mockService: VirtualRTMockService,
    private readonly stateService: StateService,
  ) {}

  async startCommand(
    plantId: string,
    emsId: string,
    systemId: string,
    monitoringType: RTMonitoringType,
    duration = 120,
  ): Promise<VirtualRTStateType> {
    this.log('startCommand', `START plant=${plantId}, system=${systemId}, ems=${emsId}, mode=${monitoringType}, duration=${duration}`);

    let status: VirtualRTStateType;

    if (VIRTUAL_RT_MOCK_ENABLED) {
      status = await this.mockService.activateMock(emsId, duration, systemId);
      this.log('startCommand', 'Mock activation result:', status);
    } else {
      if (monitoringType === 'Connext') {
        // Connext firmware caps RT sessions at 120s regardless of the requested duration.
        await this.manageConnextVirtualRT(plantId, emsId, true, Math.min(duration, 120));
      } else {
        await this.cmdAzzurroHubService.manageVirtualRT(
          emsId,
          true,
          duration,
          VirtualRTAzzurroHubTarget.ZZ,
        );
      }

      status = await this.waitForBackendVirtualRTStatus(emsId, systemId);
      this.log('startCommand', 'Backend virtualRTStatus propagated:', status);
    }

    return normalizeVirtualRTStatus(status, systemId);
  }

  async stopCommand(
    plantId: string,
    emsId: string,
    systemId: string,
    monitoringType: RTMonitoringType,
  ): Promise<VirtualRTStateType> {
    this.log('stopCommand', `STOP plant=${plantId}, system=${systemId}, ems=${emsId}, mode=${monitoringType}`);

    if (VIRTUAL_RT_MOCK_ENABLED) {
      const mockStatus = await this.mockService.deactivateMock(emsId, systemId);
      return normalizeVirtualRTStatus(mockStatus, systemId);
    }

    if (monitoringType === 'Connext') {
      await this.manageConnextVirtualRT(plantId, emsId, false, 120);
    } else {
      await this.cmdAzzurroHubService.manageVirtualRT(
        emsId,
        false,
        undefined,
        VirtualRTAzzurroHubTarget.ZZ,
      );
    }

    return {
      active: false,
      startTS: 0,
      duration: 0,
      target: systemId,
    };
  }

  createTelemetryStream(emsId: string, systemId: string): Observable<{ systemId: string; data: InverterV2 }> {
    if (VIRTUAL_RT_MOCK_ENABLED) {
      return this.mockService.subscribeMock(emsId).pipe(
        map((payload) => ({
          systemId,
          data: payload.ZZ as InverterV2,
        })),
      );
    }

    return this.onVirtualRTTelemetrySubscription
      .execute(virtualRTTelemetryFragment, { loggerId: emsId })
      .pipe(
        first(),
        switchMap((d: any) => new Observable<VirtualRTTelemetryType>(observer => {
          const zenSub = d.subscribe({
            next: (result: any) => observer.next(result?.data?.onVirtualRTTelemetry),
            error: (err: any) => observer.error(err),
            complete: () => observer.complete(),
          });

          return () => {
            if (zenSub && typeof zenSub.unsubscribe === 'function') {
              zenSub.unsubscribe();
            }
          };
        })),
        map((payload) => ({
          systemId,
          data: payload?.ZZ as InverterV2,
        })),
      );
  }

  private async waitForBackendVirtualRTStatus(emsId: string, systemId: string): Promise<VirtualRTStateType> {
    let pollCount = 0;

    return await new Promise<VirtualRTStateType>((resolve, reject) => {
      let subscription: Subscription | null = null;

      const timeoutId = setTimeout(() => {
        subscription?.unsubscribe();
        reject(new Error(`[RealTimeTelemetryService] virtualRTStatus not propagated within ${WAIT_STATUS_TIMEOUT_MS}ms for ems=${emsId}`));
      }, WAIT_STATUS_TIMEOUT_MS);

      subscription = this.stateService.awsRawData$.subscribe((awsPlants) => {
        pollCount++;
        const status = this.findVirtualRTStatusInAwsPlants(awsPlants, emsId);

        if (!status?.active) {
          return;
        }

        this.log('waitForBackendVirtualRTStatus', `status found after ${pollCount} polls for ems=${emsId}`);
        clearTimeout(timeoutId);
        subscription?.unsubscribe();
        resolve(normalizeVirtualRTStatus(status, systemId));
      });
    });
  }

  private findVirtualRTStatusInAwsPlants(awsPlants: Partial<MyCertifiedPlant>[] | null, emsId: string): VirtualRTStateType | null {
    const normalizedEmsId = emsId.toUpperCase();

    for (const plant of awsPlants ?? []) {
      const emsList = (plant?.ems_state_v2 ?? []) as Array<{ loggerId?: string; config?: { virtualRTStatus?: VirtualRTStateType } }>;
      for (const ems of emsList) {
        const emsStateId = ems?.loggerId?.toUpperCase();
        if (emsStateId !== normalizedEmsId) {
          continue;
        }

        const status = ems?.config?.virtualRTStatus;
        if (status) {
          return status;
        }
      }
    }

    return null;
  }

  private async manageConnextVirtualRT(plantId: string, connextId: string, active: boolean, duration: number): Promise<{ success?: any; error?: any; }> {
    const cmd = `set virtualRT ${active ? 'true' : 'false'} ${duration} ZZ`;
    const loadingMessage = active ? 'Starting Real-Time telemetry' : 'Stopping Real-Time telemetry';
    const result = await this.cmdConnextService.sendCommandInPlantStatusWithService(plantId, connextId, cmd, {
      timeout: 80000,
      errorMessage: 'Timeout exceeded',
      loading: true,
      loadingMessage,
    });

    if (result?.error || !result?.success?.success) {
      throw result?.error ?? new Error('Connext Virtual RT command failed');
    }

    return result;
  }

  private log(method: string, msg: string, ...args: any[]): void {
    if (!RealTimeTelemetryService.DEBUG) { return; }
    console.log(`${RealTimeTelemetryService.TAG} ${method}: ${msg}`, ...args);
  }

  private static toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  // --- Mapping utilities ---

  /**
   * Maps RT data to the partial inverter format used by BuildPowerFlow.
   * The caller (in ngx-zcs-blocks) should pass this to BuildPowerFlow() to get SystemPowerState.
   */
  private static mapToPowerFields(rtData: InverterV2): Record<string, string | undefined> {
    return {
      P_DC_TOT: `${RealTimeTelemetryService.toNumber(rtData?.P_DC_TOT)}`,
      P_EXP: `${RealTimeTelemetryService.toNumber(rtData?.P_EXP)}`,
      P_LOAD: `${RealTimeTelemetryService.toNumber(rtData?.P_LOAD)}`,
      P_B_AGGR: `${RealTimeTelemetryService.toNumber(rtData?.P_B_AGGR)}`,
      BP: `${RealTimeTelemetryService.toNumber(rtData?.BP)}`,
      TS: `${RealTimeTelemetryService.toNumber(rtData?.TS)}`,
      SOL_IRR: rtData?.SOL_IRR != null ? `${RealTimeTelemetryService.toNumber(rtData.SOL_IRR)}` : undefined,
      SOL_TEMP: rtData?.SOL_TEMP != null ? `${RealTimeTelemetryService.toNumber(rtData.SOL_TEMP)}` : undefined,
    };
  }

  static mapToInverterForPowerFlow(rtData: InverterV2): Record<string, string | undefined> {
    return RealTimeTelemetryService.mapToPowerFields(rtData);
  }

  static mapToTotalizerEnergy(rtData: InverterV2): SystemEnergyState {
    const ET1 = RealTimeTelemetryService.toNumber(rtData?.ET1);
    const ET2 = RealTimeTelemetryService.toNumber(rtData?.ET2);
    const ET3 = RealTimeTelemetryService.toNumber(rtData?.ET3);
    const ET4 = RealTimeTelemetryService.toNumber(rtData?.ET4);
    const ET5 = RealTimeTelemetryService.toNumber(rtData?.ET5);
    const ET6 = RealTimeTelemetryService.toNumber(rtData?.ET6);
    const EG1 = RealTimeTelemetryService.toNumber(rtData?.EG1);
    const EG2 = RealTimeTelemetryService.toNumber(rtData?.EG2);
    const EG3 = RealTimeTelemetryService.toNumber(rtData?.EG3);
    const EG4 = RealTimeTelemetryService.toNumber(rtData?.EG4);
    const EG5 = RealTimeTelemetryService.toNumber(rtData?.EG5);
    const EG6 = RealTimeTelemetryService.toNumber(rtData?.EG6);
    const TS = RealTimeTelemetryService.toNumber(rtData?.TS);

    return {
      ET1, ET2, ET3,
      ET4, ET5, ET6,
      EG1, EG2, EG3,
      EG4, EG5, EG6,
      energyTotalGenerating: ET1,
      energyTotalExporting: ET2,
      energyTotalImporting: ET3,
      energyTotalConsuming: ET4,
      energyTotalCharging: ET5,
      energyTotalDischarging: ET6,
      energyDailyGenerating: EG1,
      energyDailyExporting: EG2,
      energyDailyImporting: EG3,
      energyDailyConsuming: EG4,
      energyDailyCharging: EG5,
      energyDailyDischarging: EG6,
      ts: TS,
    };
  }

  static mapToDailyEnergy(rtData: InverterV2): Record<string, string> {
    return {
      EG1: `${RealTimeTelemetryService.toNumber(rtData?.EG1)}`,
      EG2: `${RealTimeTelemetryService.toNumber(rtData?.EG2)}`,
      EG3: `${RealTimeTelemetryService.toNumber(rtData?.EG3)}`,
      EG4: `${RealTimeTelemetryService.toNumber(rtData?.EG4)}`,
      EG5: `${RealTimeTelemetryService.toNumber(rtData?.EG5)}`,
      EG6: `${RealTimeTelemetryService.toNumber(rtData?.EG6)}`,
      TS: `${RealTimeTelemetryService.toNumber(rtData?.TS)}`,
    };
  }

  static mapToDailyPowerPoint(rtData: InverterV2): Record<string, string | undefined> {
    return RealTimeTelemetryService.mapToPowerFields(rtData);
  }
}
